import axios from "axios";
import config from "../config";
import * as url from "../helpers/url_helper";


const { api } = config;

// Check if we should use the fake backend
export interface LeadAttachmentItem {
  id: string;
  tenantId: string;
  tenantName?: string;
  leadId: string;
  leadTitle?: string;
  fileName: string;
  filePath: string;
  fileSizeBytes: number;
  isDeleted: boolean;
}

export interface LeadAttachmentResponse {
  items: LeadAttachmentItem[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface LeadAttachmentCreatePayload {
  tenantId: string;
  leadId: string;
  attachment: File;
}

export interface LeadAttachmentApiResponse {
  id: string;
  isSuccess: boolean;
  notFound: boolean;
  message: string;
  modelErrors: any[];
  result: LeadAttachmentItem;
}

// Get auth token from Redux Persist
const getAuthToken = () => {
  try {
    const persistedState = sessionStorage.getItem('persist:root');
    if (persistedState) {
      const parsed = JSON.parse(persistedState);
      if (parsed.Login) {
        const loginState = JSON.parse(parsed.Login);
        if (loginState.user && Object.keys(loginState.user).length > 0) {
          return loginState.user.token || null;
        }
      }
    }
  } catch (e) {
    console.error("Error reading auth token from Redux Persist:", e);
  }
  return null;
};

// Get all attachments for a lead
export const getLeadAttachments = async (
  leadId: string,
  pageNumber: number = 1,
  pageSize: number = 100
): Promise<LeadAttachmentResponse> => {
  try {
    const token = getAuthToken();
    // API endpoint: /api/v1/LeadAttachment/Lead/{leadId}
    const response = await axios.get(`${api.API_URL}${url.GET_LEAD_ATTACHMENTS}/${leadId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
      },
    });
    
    // API returns a simple array of attachments: [{id, tenantId, leadId, fileName, ...}, ...]
    // Axios interceptor already unwraps response.data, so response IS the array
    const attachments: LeadAttachmentItem[] = Array.isArray(response) ? response : [];
    
    // Filter out deleted items
    const activeAttachments = attachments.filter((item: LeadAttachmentItem) => !item.isDeleted);
    
    return {
      items: activeAttachments,
      pageNumber,
      pageSize,
      totalCount: activeAttachments.length,
      totalPages: Math.ceil(activeAttachments.length / pageSize),
      hasPreviousPage: pageNumber > 1,
      hasNextPage: activeAttachments.length > pageNumber * pageSize,
    };
  } catch (error) {
    // Return empty structure on error
    return {
      items: [],
      pageNumber,
      pageSize,
      totalCount: 0,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    };
  }
};

// Upload a new attachment
export const uploadLeadAttachment = async (
  payload: LeadAttachmentCreatePayload
): Promise<LeadAttachmentItem> => {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append("TenantId", payload.tenantId);
  formData.append("LeadId", payload.leadId);
  formData.append("Attachment", payload.attachment);

  const response = await axios.post<LeadAttachmentApiResponse>(
    `${api.API_URL}${url.ADD_LEAD_ATTACHMENT}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
        "Content-Type": "multipart/form-data",
      },
    }
  );
  
  // Axios interceptor may unwrap response.data, so handle both cases
  const responseData = (response as any).data || response;
  
  // API returns: { id, isSuccess, message, result: { ...attachment data... } }
  if (responseData?.result) {
    return responseData.result as LeadAttachmentItem;
  }
  
  // If no wrapper, check if responseData itself is the attachment (has id, fileName, etc.)
  if (responseData?.id && responseData?.fileName) {
    return responseData as LeadAttachmentItem;
  }
  
  // If structure doesn't match, throw error
  throw new Error(responseData?.message || "Invalid response from server: missing attachment data");
};

// Delete an attachment
export const deleteLeadAttachment = async (attachmentId: string): Promise<void> => {
  const token = getAuthToken();
  await axios.delete(`${api.API_URL}${url.DELETE_LEAD_ATTACHMENT}/${attachmentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json;odata.metadata=minimal;odata.streaming=true",
    },
  });
};

// Download attachment file - uses direct filePath URL
export const downloadLeadAttachment = async (
  attachment: LeadAttachmentItem
): Promise<Blob> => {
  const token = getAuthToken();
  // Download directly from filePath URL
  const response = await axios.get(attachment.filePath, {
    responseType: "blob",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};
